import { useApolloClient } from '@apollo/client'
import produce from 'immer'
import { v1 as uuid } from 'uuid'

import {
  AnnotationCommentCountFragment,
  AnnotationCommentCountFragmentDoc,
  GetFrameAnnotationInfoDocument,
  GetFrameAnnotationInfoQuery,
  GetFrameAnnotationInfoQueryVariables,
  useCreateAnnotationCommentMutation,
} from '@sketch/gql-types'

import { dataIdFromObject } from '@sketch/graphql-cache'
import { errorPreventiveFragmentRead } from '@sketch/modules-common'

import { updateCommentsQuery } from '../utils/cache'

import {
  createCommentFragment,
  createInitialUserQuery,
  hasOptimisticResponseVariables,
} from '../utils'

const useCreateAnnotationComment = (annotationIdentifier: string) => {
  const apollo = useApolloClient()
  const [createComment] = useCreateAnnotationCommentMutation({
    onCompleted: data => {
      const frameUUID =
        data?.createAnnotationReply?.comment.annotation.frameUuid
      const shareIdentifier =
        data?.createAnnotationReply?.comment.annotation.share.identifier
      if (frameUUID && shareIdentifier) {
        apollo.query<
          GetFrameAnnotationInfoQuery,
          GetFrameAnnotationInfoQueryVariables
        >({
          query: GetFrameAnnotationInfoDocument,
          variables: {
            frameUUID,
            shareIdentifier,
          },
        })
      }
    },
    onError: 'show-toast',
  })

  return (...args: Parameters<typeof createComment>) => {
    const commentIdentifier = uuid()

    return createComment({
      ...args[0],
      update: (cache, { data }) => {
        /**
         *  1st step update the annotation query
         */
        const comment = data?.createAnnotationReply.comment

        if (!comment) {
          // If there's no comment response no need to update anything then
          return
        }

        updateCommentsQuery(cache, { annotationIdentifier }, data => {
          if (!data?.annotation) {
            // Preventive measure
            return
          }

          const { comments } = data.annotation

          /**
           * Prevent having 2 annotations with the
           * same identifier added
           */
          const commentAlreadyExists = comments.entries.find(
            ({ identifier }) => comment.identifier === identifier
          )

          if (commentAlreadyExists) {
            return
          }

          // If we reach the end of the list and there's no comment updating
          // means this is the optimistic comment, let's add it then
          comments.entries.unshift(comment)
          comments.meta.totalCount = comments.meta.totalCount + 1
        })

        /**
         * 2nd step update the annotation reply count if loaded
         */
        const annotationCacheId = dataIdFromObject({
          __typename: 'Annotation',
          identifier: annotationIdentifier,
        })!

        // Read the annotation pagination
        const annotationPagination =
          errorPreventiveFragmentRead<AnnotationCommentCountFragment>(apollo, {
            fragment: AnnotationCommentCountFragmentDoc,
            id: annotationCacheId,
          })

        if (!annotationPagination) {
          // If the fragment doesn't exist we don't update :)
          return
        }

        const updatePagination = produce(
          annotationPagination,
          ({ comments }) => {
            comments.meta.totalCount = comments.meta.totalCount + 1
          }
        )

        cache.writeFragment({
          fragment: AnnotationCommentCountFragmentDoc,
          id: annotationCacheId,
          data: updatePagination,
        })
      },
      optimisticResponse: variables => {
        if (!hasOptimisticResponseVariables(variables)) {
          throw new Error(
            'Missing variables in optimistic update (`createAnnotationComment` mutation)'
          )
        }

        const { body, identifier } = variables

        const user = createInitialUserQuery(apollo)

        const comment = createCommentFragment(body, user.me, commentIdentifier)

        return {
          __typename: 'RootMutationType',
          createAnnotationReply: {
            __typename: 'CreateAnnotationReplyResponse',
            comment: {
              ...comment,
              annotation: {
                __typename: 'Annotation',
                subscriptionStatus: 'ON',
                latestCommentCreatedAt: comment.createdAt,
                identifier,

                /* We don't really need to update the share in a optimized way */
                page: {
                  __typename: 'UnprocessedPage',
                },
                frameUuid: 'some-fake-frame-uuid',
                share: {
                  __typename: 'Share',
                  identifier: 'some-fake-identifier',
                  subscriptionStatus: 'PARTIAL',
                },
              },
            },
          },
        }
      },
    })
  }
}

export default useCreateAnnotationComment
